import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import {
  homeRoutes,
  servicesRoutes,
  industriesRoutes,
  resourcesRoutes,
  legalRoutes,
  graderRoutes,
  aiRoutes,
  otherRoutes,
  catchAllRoute
} from './routeConfig';

const routeGroups = [
  homeRoutes,
  servicesRoutes,
  industriesRoutes,
  resourcesRoutes,
  legalRoutes,
  graderRoutes,
  aiRoutes,
  otherRoutes
];

function AppRouter() {
  const location = useLocation();
  const isCheckoutRedirectPath = location.pathname === '/hidden/checkout/9B600ce30cGGdF63N2gQE03';
  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, {
    stiffness: 130,
    damping: 28,
    mass: 0.18
  });

  useEffect(() => {
    if (location.hash) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const magneticSelector = '.cta-btn, .primary-btn, .secondary-btn, .service-cta a, .btn-try-again';
    const elements = Array.from(document.querySelectorAll(magneticSelector));
    const cleanupFns = [];

    elements.forEach((el) => {
      const element = el;
      element.style.willChange = 'transform, translate';
      element.style.transition = 'translate 180ms ease';

      const handleMove = (event) => {
        const rect = element.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const deltaX = (x - rect.width / 2) / 10;
        const deltaY = (y - rect.height / 2) / 10;
        element.style.translate = `${deltaX}px ${deltaY}px`;
      };

      const handleLeave = () => {
        element.style.translate = '0 0';
      };

      element.addEventListener('mousemove', handleMove);
      element.addEventListener('mouseleave', handleLeave);

      cleanupFns.push(() => {
        element.removeEventListener('mousemove', handleMove);
        element.removeEventListener('mouseleave', handleLeave);
        element.style.translate = '0 0';
      });
    });

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      {!isCheckoutRedirectPath && (
        <div className="scroll-progress-track" aria-hidden="true">
          <motion.div className="scroll-progress-bar" style={{ scaleX: progressScale }} />
        </div>
      )}

      <motion.div
        key={location.pathname}
        className="route-shell"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      >
        <Routes location={location}>
          {routeGroups.flat().map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route path={catchAllRoute.path} element={catchAllRoute.element} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default AppRouter;
